import {
  ComponentLinkHandler,
  RedactedFieldDisplay,
  EnhancedScopeDataWithBranchName,
  EntitiesList,
  EntityRepresentation,
  EntityRepresentationProps,
  FieldType,
  Graphics,
  graphics,
  GremlinPathItem,
  GremlinPathRenderer,
  Renderer,
  RenderOption,
} from '@ardoq/renderers';
import {
  APIEntityType,
  ArdoqId,
  ReportColumn,
  ReportColumnType,
  UserDataForRenderers,
} from '@ardoq/api-types';
import {
  getNavigationOptionsForTargetComponent,
  isComponentRow,
  isReferenceColumn,
  isReferenceRow,
  renderRawColumnValue,
} from './utils';
import { ReactNode } from 'react';
import {
  DataSourceRow,
  DerivedDataForReferences,
  GetComponentNavigationOptions,
  HasComponentReadPermission,
} from './types';
import { Maybe } from '@ardoq/common-helpers';
import { colors } from '@ardoq/design-tokens';

const renderOptions = {
  definitions: {
    [RenderOption.TEXT_ELLIPSIS]: {
      maxLength: 480,
      showInPopover: true,
    },
  },
  types: {
    [FieldType.TEXT_AREA]: new Set([RenderOption.TEXT_ELLIPSIS]),
  },
};

const prepareEntityRepresentationProps = ({
  columnName,
  row,
  graphics,
  enhancedScopeData,
  getComponentLinkHandler,
  getComponentNavigationOptions,
  underlineOnHover,
  variant,
}: {
  columnName: string;
  row: DataSourceRow;
  graphics: Graphics;
  enhancedScopeData: EnhancedScopeDataWithBranchName;
  getComponentLinkHandler: (
    componentId: ArdoqId
  ) => ComponentLinkHandler | undefined;
  getComponentNavigationOptions?: GetComponentNavigationOptions;
  underlineOnHover?: boolean;
  variant: 'default' | 'light';
}): Maybe<EntityRepresentationProps & { key: string }> => {
  const { _id, ardoq } = row;

  const common = {
    graphics,
    enhancedScopeData,
    shouldHaveWrapper: true,
    entityType: APIEntityType.COMPONENT,
    underlineOnHover,
    linkColor: variant === 'light' ? colors.textDefault : undefined,
    variant,
  };

  if (
    isReferenceRow(ardoq) &&
    (columnName === 'source' || columnName === 'target') &&
    row[columnName]
  ) {
    const entityId = row[columnName];
    return {
      ...common,
      key: entityId,
      entityId,
      componentLinkHandler: getComponentLinkHandler(entityId),
      navigationOptions: getNavigationOptionsForTargetComponent({
        enhancedScopeData,
        targetId: entityId,
        getComponentNavigationOptions,
      }),
    };
  }

  if (isComponentRow(ardoq)) {
    const parent =
      row.parent ?? enhancedScopeData.componentsById[row._id]?.parent;

    if (columnName === 'name') {
      return {
        ...common,
        key: _id,
        entityId: _id,
        parentEntityId: parent ?? undefined,
        componentLinkHandler: getComponentLinkHandler(_id),
        navigationOptions: getNavigationOptionsForTargetComponent({
          enhancedScopeData,
          targetId: _id,
          getComponentNavigationOptions,
        }),
      };
    } else if (columnName === 'parent' && parent) {
      return {
        ...common,
        key: parent,
        entityId: parent,
        componentLinkHandler: getComponentLinkHandler(parent),
        navigationOptions: getNavigationOptionsForTargetComponent({
          enhancedScopeData,
          targetId: parent,
          getComponentNavigationOptions,
        }),
      };
    }
  }

  return null;
};

/**
 * @deprecated This is a temporary solution until the API for the confidential fields has consolidated.
 */
const isRedactedField = (
  rowData: DataSourceRow,
  columnKey: string
): boolean => {
  return rowData.ardoq?.redactedFields?.includes(columnKey) ?? false;
};

type GetValueRender = (parameters: {
  column: ReportColumn;
  enhancedScopeData: EnhancedScopeDataWithBranchName | null;
  isInteractive: boolean;
  isEntityNavigationDisabled?: boolean;
  getComponentLinkHandler?: (
    componentId: ArdoqId
  ) => ComponentLinkHandler | undefined;
  getComponentNavigationOptions?: GetComponentNavigationOptions;
  users: Record<ArdoqId, UserDataForRenderers>;
  hasComponentReadPermission?: HasComponentReadPermission;
  underlineOnHover?: boolean;
  variant: 'default' | 'light';
}) => (_: unknown, rowData: DataSourceRow) => ReactNode;

const getValueRender: GetValueRender =
  ({
    column,
    enhancedScopeData,
    isInteractive,
    isEntityNavigationDisabled = false,
    getComponentLinkHandler,
    getComponentNavigationOptions,
    users,
    hasComponentReadPermission,
    underlineOnHover,
    variant,
  }) =>
  (_, rowData) => {
    if (!enhancedScopeData) {
      return renderRawColumnValue(
        rowData[column.key],
        column.dataType,
        underlineOnHover
      ) as ReactNode;
    }

    if (!isComponentRow(rowData.ardoq) && column.key === 'name') {
      return rowData.name;
    }

    if (isRedactedField(rowData, column.key)) {
      return <RedactedFieldDisplay />;
    }

    const getComponentLinkHandlerWithPermissionChecks = (
      componentId: ArdoqId
    ) => {
      if (
        isInteractive &&
        !isEntityNavigationDisabled &&
        getComponentLinkHandler &&
        enhancedScopeData &&
        hasComponentReadPermission?.(componentId)
      ) {
        return getComponentLinkHandler(componentId);
      }
      return undefined;
    };

    const getNavigationOptions = isEntityNavigationDisabled
      ? undefined
      : getComponentNavigationOptions;

    if (isReferenceColumn(column)) {
      const derivedDataForColumn =
        (rowData.derivedData?.[column.key] as DerivedDataForReferences) ?? [];
      // TODO: once API is deployed, we can remove this logic and use derivedDataForColumn as value for componentIds
      const componentIds = Array.isArray(derivedDataForColumn)
        ? derivedDataForColumn
        : (derivedDataForColumn?.[
            column.type === ReportColumnType.REFERENCE_TYPE_INCOMING
              ? 'incoming'
              : 'outgoing'
          ] ?? []);

      return (
        <EntitiesList
          entityIds={componentIds}
          entityType={APIEntityType.COMPONENT}
          enhancedScopeData={enhancedScopeData}
          graphics={graphics}
          options={renderOptions}
          getComponentLinkHandler={getComponentLinkHandlerWithPermissionChecks}
          getComponentNavigationOptions={getNavigationOptions}
          hideDefaultPopover={isEntityNavigationDisabled}
          underlineOnHover={underlineOnHover}
          variant={variant}
        />
      );
    } else if (column.type === ReportColumnType.GREMLIN_PATH) {
      return (
        <GremlinPathRenderer
          hideDefaultPopover={isEntityNavigationDisabled}
          getComponentLinkHandler={getComponentLinkHandlerWithPermissionChecks}
          gremlinPathArray={
            (rowData.derivedData?.[column.key] as GremlinPathItem[]) || []
          }
          enhancedScopeData={enhancedScopeData}
          underlineOnHover={underlineOnHover}
          variant={variant}
        />
      );
    }

    const entityRepresentationPropsWithKey = prepareEntityRepresentationProps({
      columnName: column.key,
      row: rowData,
      graphics,
      enhancedScopeData,
      getComponentLinkHandler: getComponentLinkHandlerWithPermissionChecks,
      getComponentNavigationOptions: getNavigationOptions,
      underlineOnHover,
      variant,
    });

    if (entityRepresentationPropsWithKey) {
      const { key, ...entityRepresentationProps } =
        entityRepresentationPropsWithKey;
      return (
        <EntityRepresentation
          key={key}
          {...entityRepresentationProps}
          hideDefaultPopover={isEntityNavigationDisabled}
        />
      );
    }

    return (
      <Renderer
        entityId={rowData._id}
        entityType={
          isComponentRow(rowData.ardoq)
            ? APIEntityType.COMPONENT
            : APIEntityType.REFERENCE
        }
        fieldName={column.key}
        enhancedScopeData={enhancedScopeData}
        users={users}
        graphics={graphics}
        options={renderOptions}
        underlineOnHover={underlineOnHover}
        variant={variant}
      />
    );
  };

export { getValueRender };
